export interface MarkdownEditorSelection {
  start: number
  end: number
  selectedText: string
}

export function getMarkdownEditorSelection(container: HTMLElement | null): MarkdownEditorSelection | null {
  const textarea = container?.querySelector('textarea')
  if (!textarea) return null

  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  return {
    start,
    end,
    selectedText: textarea.value.slice(start, end),
  }
}

export function insertMarkdownAtSelection({
  container,
  currentValue,
  snippet,
  selection,
}: {
  container: HTMLElement | null
  currentValue: string
  snippet: string
  selection: MarkdownEditorSelection
}): string {
  const hadSelection = selection.start !== selection.end
  const nextValue =
    currentValue.slice(0, selection.start) +
    (hadSelection ? snippet : ` ${snippet} `) +
    currentValue.slice(selection.end)

  requestAnimationFrame(() => {
    const textarea = container?.querySelector('textarea')
    if (!textarea) return
    textarea.focus()
    const cursor = selection.start + snippet.length + (hadSelection ? 0 : 2)
    textarea.setSelectionRange(cursor, cursor)
  })

  return nextValue
}

function getInsertPositionAfterLine(value: string, position: number): number {
  const lineEnd = value.indexOf('\n', position)
  return lineEnd === -1 ? value.length : lineEnd + 1
}

function getInsertPositionEndOfLine(value: string, position: number): number {
  const lineEnd = value.indexOf('\n', position)
  return lineEnd === -1 ? value.length : lineEnd
}

export function insertSectionAudioInline({
  container,
  currentValue,
  snippet,
  selection,
}: {
  container: HTMLElement | null
  currentValue: string
  snippet: string
  selection: MarkdownEditorSelection
}): string {
  const insertAt = getInsertPositionEndOfLine(currentValue, selection.start)
  const inlineSnippet = `  ${snippet.trim()}`
  const nextValue = currentValue.slice(0, insertAt) + inlineSnippet + currentValue.slice(insertAt)

  requestAnimationFrame(() => {
    const textarea = container?.querySelector('textarea')
    if (!textarea) return
    textarea.focus()
    const cursor = insertAt + inlineSnippet.length
    textarea.setSelectionRange(cursor, cursor)
  })

  return nextValue
}

export function insertSectionAudioAfterLine({
  container,
  currentValue,
  snippet,
  selection,
}: {
  container: HTMLElement | null
  currentValue: string
  snippet: string
  selection: MarkdownEditorSelection
}): string {
  const insertAt = getInsertPositionAfterLine(currentValue, selection.start)
  const block = snippet.endsWith('\n') ? snippet : `${snippet}\n`
  const nextValue = currentValue.slice(0, insertAt) + block + currentValue.slice(insertAt)

  requestAnimationFrame(() => {
    const textarea = container?.querySelector('textarea')
    if (!textarea) return
    textarea.focus()
    const cursor = insertAt + block.length
    textarea.setSelectionRange(cursor, cursor)
  })

  return nextValue
}

export function insertMarkdownAudio({
  container,
  currentValue,
  snippet,
  selection,
  mode,
}: {
  container: HTMLElement | null
  currentValue: string
  snippet: string
  selection: MarkdownEditorSelection
  mode: 'phrase' | 'section'
}): string {
  if (mode === 'section') {
    return insertSectionAudioInline({ container, currentValue, snippet, selection })
  }

  return insertMarkdownAtSelection({ container, currentValue, snippet, selection })
}
